import React, { useEffect, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { uploadFieldsExperiment } from "services";

import {
  SHAPEFILE_VALIDATION_ERRORS,
  addShapeProcessor,
  checkValidShapefileSize,
  checkValidShapefileType,
  handleFileUpload,
  useAppType,
  useGetFarms,
  useTranslation,
} from "utils";

import { GET_FIELDS } from "consts";

import {
  Button,
  Col,
  Dragger,
  ExtraInfo,
  Form,
  Icon,
  LazyLoadImage,
  Link,
  LinkMoreInfo,
  Row,
  ScreenSuccess,
  Spin,
  StickyFooterButtons,
  Message as message,
} from "components";

import { FieldDetails } from "pages/app/fields/components/FieldDetails";

const SHAPEFILE_INFO_TYPE = "shapefile-upload-guidance";

export const FieldUpload = ({ onFinish }: { onFinish: () => void }) => {
  const { t, ready } = useTranslation(["onboarding", "enums"], { useSuspense: false });
  const { mobile } = useAppType();
  const queryClient = useQueryClient();
  const { currentFarm } = useGetFarms();

  const [fieldUploadStep, setFieldUploadStep] = useState("INTRO");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toggleModuleExplainerModal, setToggleModuleExplainerModal] = useState(false);
  const [upload, setUpload] = useState("");
  const [uploadURL, setUploadURL] = useState("");
  const [uploadPercent, setUploadPercent] = useState(0);
  const [showShapefileInfoDrawer, setShowShapefileInfoDrawer] = useState(false);

  const SIMPLE_FORMAT_ISSUES = new Set([
    SHAPEFILE_VALIDATION_ERRORS.INVALID_EXTENSION,
    SHAPEFILE_VALIDATION_ERRORS.INVALID_SIGNATURE,
    SHAPEFILE_VALIDATION_ERRORS.MISSING_REQUIRED_FILE,
    SHAPEFILE_VALIDATION_ERRORS.NESTED_ZIP,
  ]);

  const getFormattedErrorMessage = (reason?: keyof typeof SHAPEFILE_VALIDATION_ERRORS, meta?: string) => {
    switch (reason) {
      case SHAPEFILE_VALIDATION_ERRORS.NESTED_ZIP:
        return t("onboarding:uploadFileNestedZip");
      case SHAPEFILE_VALIDATION_ERRORS.MISSING_REQUIRED_FILE:
        return t("onboarding:uploadFileInvalidStructure", { fileType: meta?.toLowerCase() });
      case SHAPEFILE_VALIDATION_ERRORS.INVALID_SIGNATURE:
        return t("onboarding:uploadFileInvalidSignature");
      case SHAPEFILE_VALIDATION_ERRORS.INVALID_EXTENSION:
        return t("onboarding:uploadFileInvalidExtension");
      default:
        return t("onboarding:uploadFileWrongArchive");
    }
  };

  const handleFormatIssue = (reason?: string, meta?: string) => {
    const shouldOpenDrawer = reason && reason !== SHAPEFILE_VALIDATION_ERRORS.NESTED_ZIP && SIMPLE_FORMAT_ISSUES.has(reason ?? "");

    message({
      content: getFormattedErrorMessage(reason as keyof typeof SHAPEFILE_VALIDATION_ERRORS, meta),
      icon: "error",
      onClose: () => {
        if (shouldOpenDrawer) {
          setShowShapefileInfoDrawer(true);
        }
      },
    });
  };

  const simulateProgress = () => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 8 + 2; // Random increment between 2-10%
      if (progress >= 95) {
        progress = 95; // Stop at 95% until actual completion
        clearInterval(interval);
      }
      setUploadPercent(() => Math.floor(Math.min(progress, 95)));
    }, 500);

    return interval;
  };

  const handleUpload = (file: File) => {
    setSaving(true);
    handleFileUpload({
      file,
      location: "test-shapefiles",
    })
      .then((res) => {
        setUploadURL(res.location);
        setUpload("success");
        message({ content: t("uploadFileSuccess"), icon: "tick" });
      })
      .finally(() => {
        setLoading(false);
        setSaving(false);
      });
  };

  const fileUpload = async (file: File) => {
    const typeValidation = await checkValidShapefileType(file);
    const isValidSize = await checkValidShapefileSize(file);

    if (!typeValidation.isValid) {
      handleFormatIssue(typeValidation.reason, typeValidation.meta ?? undefined);
      return false;
    }

    if (!isValidSize) {
      message({
        content: t("invekosTooLarge"),
        icon: "error",
      });
      return false;
    }

    handleUpload(file);
    return false;
  };

  const complete = async () => {
    setSaving(true);
    setUpload("inProgress");
    setUploadPercent(0);

    const progressInterval = simulateProgress();

    const uploadAllFields = async () => {
      try {
        await uploadFieldsExperiment({
          file: uploadURL,
        });
        clearInterval(progressInterval);
        setUploadPercent(100);
      } catch {
        setUpload("failed");
        setSaving(false);
        clearInterval(progressInterval);
      }
      // Invalidate get field query, so fields are reloaded in whatever component they are used
      queryClient.invalidateQueries({ queryKey: [GET_FIELDS] });
      queryClient.invalidateQueries({ queryKey: ["getFieldShapes", currentFarm?.id] });
    };

    uploadAllFields();
  };

  const incomplete = () => {
    message({ content: t("errors.mandatoryFields"), icon: "error" });
  };

  useEffect(() => {
    addShapeProcessor();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    !loading &&
    ready && (
      <section className="onboarding">
        {fieldUploadStep === "INTRO" && (
          <>
            <Row gutter={[{ xs: 8, sm: 8, md: 16 }, 0]} dataTestId="onboarding-field-info">
              <Col className="margin-bottom-100">
                <LazyLoadImage alt="farm-info-photo" src={"/images/onboarding/field.jpg"} width={"100%"} />
              </Col>
              <Col>
                <h1 className={mobile ? "margin-bottom-200" : "margin-bottom-100"}>{t("onboarding:onboarding.addFarmAndField.fieldTitle")}</h1>
                <p className={mobile ? "margin-bottom-100" : "margin-bottom-225"}>{t("onboarding:onboarding.addFarmAndField.fieldDescription")}</p>

                <LinkMoreInfo dataTestId="field-upload-auto-more-info-link" onClick={() => setShowShapefileInfoDrawer(!showShapefileInfoDrawer)}>
                  {t("onboarding:invekosPopup")}
                </LinkMoreInfo>
                <Button
                  block
                  dataTestId="onboarding-step1-field-add-auto"
                  className={mobile ? "margin-top-400" : "margin-top-225"}
                  type="primary"
                  onClick={() => setFieldUploadStep("UPLOAD")}
                >
                  {t("onboarding:fieldDetails.onboardingButton.addFieldsAuto")}
                </Button>

                <Button
                  block
                  dataTestId="onboarding-step1-field-add-manually"
                  className="margin-top-100"
                  type={"secondary"}
                  onClick={() => setFieldUploadStep("MANUAL_UPLOAD")}
                >
                  {t("onboarding:fieldDetails.onboardingButton.addFieldsManually")}
                </Button>
              </Col>
            </Row>
          </>
        )}
        {/* @ts-expect-error need to convert to TS */}
        {fieldUploadStep === "MANUAL_UPLOAD" && <FieldDetails setStep={setFieldUploadStep} onFinish={onFinish} />}
        {fieldUploadStep === "UPLOAD" && (
          <Form name="upload" onFinish={complete} onFinishFailed={incomplete}>
            <section className="container tiny">
              <Row className="margin-bottom-350">
                <Col>
                  <h1>{t("uploadInvekosHeadline")}</h1>
                </Col>
                <Col>
                  <p>{t("uploadInvekosDescription")}</p>
                  <LinkMoreInfo onClick={() => setShowShapefileInfoDrawer(!showShapefileInfoDrawer)}>{t("invekosPopup")}</LinkMoreInfo>
                </Col>
              </Row>
            </section>
            <section className="container tiniest">
              <Row>
                <Col>
                  <Form.Item name="file" rules={[{ required: true }]} className="center">
                    <Dragger
                      name="file"
                      showUploadList={false}
                      action=""
                      beforeUpload={(v) => fileUpload(v)}
                      className={upload === "success" ? "success" : upload === "failed" ? "failed" : ""}
                    >
                      {!upload && (
                        <>
                          <Icon iconName="klim/plus" size="xl" />
                          <p>{t("uploadFile")}</p>
                        </>
                      )}
                      {upload === "inProgress" && (
                        <>
                          <Spin />
                          <p className="center in-progress">{uploadPercent} %</p>
                          <div className="loading-outer">
                            <div className="loading-inner" style={{ width: `${uploadPercent}%` }} />
                          </div>
                        </>
                      )}
                      {upload === "success" && (
                        <>
                          <Icon iconName="tabler/file-check" size="xl" />
                          <p>{t("uploadFileSuccess")}</p>
                        </>
                      )}
                      {upload === "failed" && (
                        <>
                          <Icon iconName="tabler/file-alert" size="xl" />
                          <p>{t("uploadFileFailed")}</p>
                        </>
                      )}
                    </Dragger>
                  </Form.Item>
                </Col>
              </Row>
            </section>
            <StickyFooterButtons handleBack={() => setFieldUploadStep("INTRO")} absolute>
              <Button type="primary" htmlType="submit" disabled={upload === "inProgress" || saving} loading={saving}>
                {t("uploadDocumentButton")}
              </Button>
            </StickyFooterButtons>
          </Form>
        )}
        {fieldUploadStep === "SUCCESS" && (
          <ScreenSuccess
            description={t("invekosUploadSuccessDescription")}
            headline={t("invekosUploadSuccessHeadline")}
            infoLinkText={t("invekosUploadSuccessInfoLink")}
            onInfoLinkClick={() => setToggleModuleExplainerModal(!toggleModuleExplainerModal)}
          >
            <Row justify="center" align="middle">
              <Col>
                <Button type="primary" loading={saving} onClick={onFinish} mixpanelText={t("invekosUploadSuccessButton")}>
                  {t("invekosUploadSuccessButton")}
                </Button>
              </Col>
            </Row>
            <Row justify="center" align="middle">
              <Col>
                <Link onClick={onFinish} mixpanelText={t("invekosUploadSkipButton")}>
                  {t("invekosUploadSkipButton")}
                </Link>
              </Col>
            </Row>
          </ScreenSuccess>
        )}

        <ExtraInfo type={SHAPEFILE_INFO_TYPE} visible={showShapefileInfoDrawer} setVisible={setShowShapefileInfoDrawer} />
      </section>
    )
  );
};
